import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { exists, fuzzyMatchObjective, sortObjectives } from "@examspark/common";
import {
  Button,
  Form,
  Text,
  TextInput,
  Toolbar,
  VFlex,
  useStabilized,
} from "@examspark/common-ui";
import { LabelLike } from "@examspark/common-ui/dist/components/inputs/Label";
import { colorFromTheme } from "@examspark/common-ui/dist/utils/color";

import { useQuerySearchActivityObjectives } from "../../../api/operations/activity";
import { useQueriesGetObjectives } from "../../../api/operations/objectives";
import { NewActivityContext } from "../../../classroom";

import {
  ActivityEditorDescriptionField,
  ActivityEditorTitleField,
} from "./ActivityEditorBasic";

const Container = styled.div`
  max-width: 50rem;
  margin: 0 auto;

  width: 100%;
  padding: 1rem;
`;

const ObjectiveItem = styled.div`
  display: flex;
  gap: 2rem;
  font-size: 1.3rem;
  padding: 0.5rem 0.7rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colorFromTheme("surface2")};
  }
`;
const ObjectiveItemInput = styled.div`
  padding: 0.7rem 0;
`;

export const ActivityBuilderForm = () => {
  const [state, dispatch] = useContext(NewActivityContext);

  const [objectiveFilter, setObjectiveFilter] = useState("");

  const objectiveAggsQuery = useQuerySearchActivityObjectives();

  const objectivesQueries = useQueriesGetObjectives(
    objectiveAggsQuery.data?.map((agg) => ({ id: agg.key, sk: "standard" })) ??
      [],
  );

  // useQueries results are not memoized, so we need to stabilize them
  // so they can be used as a dependency. The easiest way to do this is
  // to look at the IDs to know when they have changed.
  const objectives = objectivesQueries.map((q) => q.data).filter(exists);
  const stabilizedQueriesKeys = objectives.map((o) => o.id).join(",");
  const stabilizedObjectives = useStabilized(objectives, [
    stabilizedQueriesKeys,
  ]);

  useEffect(() => {
    dispatch({
      type: "PRODUCE_UPDATE",
      producer: (draft) => {
        draft.objectives = stabilizedObjectives.map((objective) => ({
          ...objective,
          id: objective.id,
          enabled: true,
          qty: "99",
          abbreviation: objective.abbreviation ?? "",
          sequence: objective.sequence ?? 0,
          content: objective.content ?? "",
          tags: objective.tags ?? [],
        }));
      },
    });
  }, [dispatch, stabilizedObjectives, stabilizedQueriesKeys]);

  return (
    <Container>
      <Form.FieldList style={{ gap: "1.2rem" }}>
        <ActivityEditorTitleField />
        <ActivityEditorDescriptionField />

        {state.objectives.length > 0 && (
          <>
            <Text.H3>Standards</Text.H3>
            <LabelLike label="Search">
              <Toolbar>
                <div style={{ flex: "1" }}>
                  <TextInput
                    placeholder="Filter Standards by Text"
                    style={{ width: "100%" }}
                    value={objectiveFilter}
                    onChange={(e) => setObjectiveFilter(e.target.value)}
                  />
                </div>
              </Toolbar>
            </LabelLike>
            <Toolbar.Right>
              <LabelLike label="Select" />
              <Button
                title="Select All"
                variant="outlined"
                onClick={() => {
                  dispatch({
                    type: "TOGGLE_OBJECTIVES",
                    enabled: true,
                  });
                }}
              >
                All
              </Button>
              <Button
                title="Select None"
                variant="outlined"
                onClick={() => {
                  dispatch({
                    type: "TOGGLE_OBJECTIVES",
                    enabled: false,
                  });
                }}
              >
                None
              </Button>
            </Toolbar.Right>
            <VFlex>
              {sortObjectives(state.objectives)
                .filter((obj) => fuzzyMatchObjective(obj, objectiveFilter))
                .map((obj) => {
                  return (
                    <ObjectiveItem
                      key={obj.id}
                      onClick={() => {
                        dispatch({
                          type: "TOGGLE_OBJECTIVE",
                          objective: obj,
                        });
                      }}
                    >
                      <ObjectiveItemInput>
                        <input
                          type="checkbox"
                          checked={obj.enabled}
                          onChange={(e) => {
                            dispatch({
                              type: "TOGGLE_OBJECTIVE",
                              objective: obj,
                              enabled: !obj.enabled,
                            });
                          }}
                        />
                      </ObjectiveItemInput>
                      {/* todo: spruce this up */}
                      <div>
                        <Text.H4>{obj.abbreviation}</Text.H4>
                        <Text.Body>{obj.content}</Text.Body>
                      </div>
                    </ObjectiveItem>
                  );
                })}
            </VFlex>
          </>
        )}
      </Form.FieldList>
    </Container>
  );
};
